"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.orderRepository = exports.OrderRepository = void 0;
const client_1 = require("../client");
class OrderRepository {
    get db() {
        return (0, client_1.getSupabase)();
    }
    /**
     * Add a single order
     */
    async addOrder(order) {
        const { data, error } = await this.db
            .from('orders')
            .insert(order)
            .select()
            .single();
        if (error)
            throw error;
        return data;
    }
    /**
     * Add multiple orders in bulk
     * Filters out balance entries automatically
     */
    async addOrders(orders) {
        // Filter out balance entries
        const validOrders = orders.filter((order) => !order.symbol.toLowerCase().includes('balance') &&
            !order.symbol.match(/^CRM-/i));
        if (validOrders.length === 0) {
            return [];
        }
        const { data, error } = await this.db
            .from('orders')
            .insert(validOrders)
            .select();
        if (error)
            throw error;
        return data;
    }
    /**
     * Upsert orders (insert or update based on ticket)
     * Filters out balance entries automatically
     */
    async upsertOrders(orders) {
        // Filter out balance entries
        const validOrders = orders.filter((order) => !order.symbol.toLowerCase().includes('balance') &&
            !order.symbol.match(/^CRM-/i));
        if (validOrders.length === 0) {
            return [];
        }
        const { data, error } = await this.db
            .from('orders')
            .upsert(validOrders, {
            onConflict: 'account_id,ticket',
            ignoreDuplicates: false,
        })
            .select();
        if (error)
            throw error;
        return data;
    }
    /**
     * Get orders by time range
     */
    async getOrdersByTimeRange(accountId, startTime, endTime, options) {
        let query = this.db
            .from('orders')
            .select('*')
            .eq('account_id', accountId)
            .gte('open_time', startTime)
            .lt('open_time', endTime);
        if (options?.status) {
            query = query.eq('status', options.status);
        }
        if (options?.limit) {
            query = query.limit(options.limit);
        }
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 100) - 1);
        }
        query = query.order('open_time', { ascending: false });
        const { data, error } = await query;
        if (error)
            throw error;
        return data;
    }
    /**
     * Get orders with pagination using continuation token
     */
    async getOrders(options) {
        console.log('📊 OrderRepository.getOrders() - Querying SUPABASE', {
            accountId: options.accountId,
            limit: options.limit,
            offset: options.offset,
            table: 'trader.orders'
        });
        const limit = options.limit || 100;
        const offset = options.offset || 0;
        let query = this.db
            .from('orders')
            .select('*', { count: 'exact' })
            .eq('account_id', options.accountId);
        // Apply filters
        if (options.status) {
            query = query.eq('status', options.status);
        }
        if (options.symbol) {
            query = query.eq('symbol', options.symbol);
        }
        if (options.startTime) {
            query = query.gte('open_time', options.startTime);
        }
        if (options.endTime) {
            query = query.lt('open_time', options.endTime);
        }
        // Apply ordering
        const orderBy = options.orderBy || 'open_time';
        const orderDirection = options.orderDirection || 'desc';
        query = query.order(orderBy, { ascending: orderDirection === 'asc' });
        // Apply pagination
        query = query.range(offset, offset + limit);
        const { data, error, count } = await query;
        console.log('📊 OrderRepository - SUPABASE response:', {
            resultCount: data?.length || 0,
            totalCount: count,
            hasError: !!error,
            errorMessage: error?.message
        });
        if (error)
            throw error;
        const hasMore = count ? offset + limit < count : false;
        const continuationToken = hasMore
            ? btoa(JSON.stringify({ offset: offset + limit }))
            : undefined;
        return {
            data: data || [],
            continuationToken,
            hasMore,
            total: count || 0,
        };
    }
    /**
     * Get orders closed in a specific time range
     */
    async getOrdersClosedInRange(accountId, startTime, endTime) {
        const { data, error } = await this.db
            .from('orders')
            .select('*')
            .eq('account_id', accountId)
            .eq('status', 'CLOSED')
            .gte('close_time', startTime)
            .lt('close_time', endTime)
            .order('close_time', { ascending: false });
        if (error)
            throw error;
        return data;
    }
    /**
     * Get orders that were open at a specific point in time
     * (opened before the time and either still open or closed after)
     */
    async getOrdersOpenAtTime(accountId, timestamp) {
        const { data, error } = await this.db
            .from('orders')
            .select('*')
            .eq('account_id', accountId)
            .lt('open_time', timestamp)
            .or(`status.eq.OPEN,close_time.gte.${timestamp}`)
            .order('open_time', { ascending: false });
        if (error)
            throw error;
        return data;
    }
    /**
     * Get a single order by ticket number.
     * Ticket numbers are unique per account, not globally, so the account is required.
     */
    async getOrderByTicket(accountId, ticket) {
        const { data, error } = await this.db
            .from('orders')
            .select('*')
            .eq('account_id', accountId)
            .eq('ticket', ticket)
            .single();
        if (error) {
            if (error.code === 'PGRST116') {
                // Not found
                return null;
            }
            throw error;
        }
        return data;
    }
    /**
     * Update an order.
     * Ticket numbers are unique per account, not globally, so the account is required.
     */
    async updateOrder(accountId, ticket, updates) {
        const { data, error } = await this.db
            .from('orders')
            .update(updates)
            .eq('account_id', accountId)
            .eq('ticket', ticket)
            .select()
            .single();
        if (error)
            throw error;
        return data;
    }
    /**
     * Delete an order.
     * Ticket numbers are unique per account, not globally, so the account is required.
     */
    async deleteOrder(accountId, ticket) {
        const { error } = await this.db
            .from('orders')
            .delete()
            .eq('account_id', accountId)
            .eq('ticket', ticket);
        if (error)
            throw error;
    }
    /**
     * Delete multiple orders by account
     */
    async deleteOrdersByAccount(accountId) {
        const { error } = await this.db
            .from('orders')
            .delete()
            .eq('account_id', accountId);
        if (error)
            throw error;
    }
    /**
     * Get order statistics for a time range
     */
    async getOrderStats(accountId, startTime, endTime) {
        // Get closed orders in range
        const closedOrders = await this.getOrdersClosedInRange(accountId, startTime, endTime);
        // Get orders open at end of range
        const openOrders = await this.getOrdersOpenAtTime(accountId, endTime);
        // Filter open orders to exclude those closed in the range
        const openOrdersFiltered = openOrders.filter((order) => order.status === 'OPEN' ||
            !order.close_time ||
            order.close_time >= endTime);
        const totalProfit = closedOrders.reduce((sum, o) => sum + (o.profit || 0), 0);
        const totalCommission = closedOrders.reduce((sum, o) => sum + (o.commission || 0), 0);
        const totalSwap = closedOrders.reduce((sum, o) => sum + (o.swap || 0), 0);
        return {
            totalOrders: closedOrders.length,
            closedOrders: closedOrders.length,
            openOrders: openOrdersFiltered.length,
            totalProfit,
            totalCommission,
            totalSwap,
        };
    }
}
exports.OrderRepository = OrderRepository;
// Export singleton instance
exports.orderRepository = new OrderRepository();
